import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Bed,
  Film,
  Gem,
  House,
  Landmark,
  Minus,
  Phone,
  Pickaxe,
  Plus,
  ShieldCheck,
  ShoppingBag,
  Truck,
  Video,
} from "lucide-react";
import MarketingShell from "../MarketingShell";
import SEOHead from "../../components/SEOHead";
import { useOpeningHours, useTourPricing, openingHoursSchema, formatPrice } from "../useBusinessInfo";
import useStoreProducts from "../store/useStoreProducts";
import {
  isOutOfStock,
  isSouvenir,
  jewelleryType,
  kicker,
  mainImage,
  opalType,
  pricing,
  sortProducts,
} from "../store/storeTaxonomy";
import { PriceLabel, STORE_PHONE, productPath } from "../store/ShopParts";
import "./home.css";

/**
 * RHomePage — home page v2, "two doors": Underground Tours and the Online
 * Opal Shop as the two main features.
 *
 * Design: design_handoff_home_v2 ("Home Page v2.dc.html").
 * Live data: tour availability (GET /api/tours/availability/{date}), tour
 * pricing and opening hours (useBusinessInfo), the published shop range
 * (useStoreProducts — also used by the shop pages, so it's cached between them).
 *
 * `lead` ("tours" | "shop") swaps which door is on the left and which feature
 * section comes first.
 */

const baseSchema = {
  "@context": "https://schema.org",
  "@type": "TouristAttraction",
  name: "Umoona Opal Mine & Museum",
  description:
    "Underground opal mine tours and authentic Australian opal shopping in Coober Pedy. Experience the world's opal capital with guided mine tours and museum exhibits.",
  address: {
    "@type": "PostalAddress",
    streetAddress: "14 Hutchison St",
    addressLocality: "Coober Pedy",
    addressRegion: "SA",
    postalCode: "5723",
    addressCountry: "AU",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: -29.0135,
    longitude: 134.753,
  },
  telephone: "+61886724588",
  url: "https://umoonaopalmine.com.au",
  priceRange: "$15-$50",
  hasMap: "https://goo.gl/maps/example",
  petsAllowed: false,
  amenityFeature: [
    {
      "@type": "LocationFeatureSpecification",
      name: "Pet Policy",
      value: "Dogs and pets are NOT permitted at Umoona Opal Mine & Museum for safety reasons",
    },
  ],
};

const API = process.env.REACT_APP_BACKEND_URL;
const TOUR_TIMES = [
  { value: "10:00", label: "10am" },
  { value: "14:00", label: "2pm" },
  { value: "16:00", label: "4pm" },
];
const MAX_GUESTS = 30;

const INCLUDES = [
  { Icon: Pickaxe, t: "Original hand-dug drives", s: "Mined by hand since the 1920s" },
  { Icon: Gem, t: "Opal in the wall", s: "See seams still in the rock" },
  { Icon: House, t: "Underground home", s: "How locals beat the 45°C heat" },
  { Icon: Film, t: "Story of Opal cinema", s: "Award-winning film, free entry" },
];

const TRUST = [
  { Icon: ShieldCheck, t: "Certificate of authenticity", s: "With every opal piece" },
  { Icon: Gem, t: "Mined in Coober Pedy", s: "Straight from the miners" },
  { Icon: Truck, t: "Australia-wide shipping", s: "From our showroom" },
  { Icon: Phone, t: "Talk to a real person", s: STORE_PHONE },
];

const ALSO = [
  { to: "/free-museum", Icon: Landmark, k: "FREE ENTRY · ALL AGES", t: "The Free Museum", mod: "green" },
  { to: "/webcam", Icon: Video, k: "● LIVE 24/7", t: "Coober Pedy Webcams", mod: "blue" },
  { to: "/group-accommodation", Icon: Bed, k: "102 BEDS · 24°C", t: "Groups & Underground Stays", mod: "teal" },
];

const REVIEWS = [
  { q: "A brilliant tour led by Zoe, a local — a must in Coober Pedy.", name: "Daryl Scales" },
  {
    q: "Beautiful museum with history of the area, its people, fossils and opals. Excellent shop as well.",
    name: "Liv Rainey-Smith",
  },
  {
    q: "We had a lovely time with the guide. All staff were friendly and helpful. Great place to visit when in Coober Pedy.",
    name: "Ayoma Dhanapala",
  },
];

const SHOP = "/online-store/shop";
const BROWSE = [
  { label: "Rings", href: `${SHOP}?type=Rings`, match: (p) => jewelleryType(p) === "Rings" },
  { label: "Pendants", href: `${SHOP}?type=Pendants`, match: (p) => jewelleryType(p) === "Pendants" },
  { label: "Earrings", href: `${SHOP}?type=Earrings`, match: (p) => jewelleryType(p) === "Earrings" },
  { label: "Solid opal", href: `${SHOP}?opal=Solid`, match: (p) => opalType(p) === "Solid" },
  {
    label: "Doublet & triplet",
    href: `${SHOP}?opal=Doublet,Triplet`,
    match: (p) => ["Doublet", "Triplet"].includes(opalType(p)),
  },
  {
    label: "Souvenirs",
    href: `${SHOP}?category=${encodeURIComponent("Souvenirs & gifts")}`,
    match: (p) => isSouvenir(p),
  },
];

// ---- Coober Pedy (Adelaide) date & time --------------------------------------

function adelaideNow() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Australia/Adelaide",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value || "0";
  const hour = parseInt(get("hour"), 10) % 24;
  return { date: `${get("year")}-${get("month")}-${get("day")}`, minutes: hour * 60 + parseInt(get("minute"), 10) };
}

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function addDays(iso, n) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return {
    iso: dt.toISOString().slice(0, 10),
    dow: DOW[dt.getUTCDay()],
    num: dt.getUTCDate(),
  };
}
const toMinutes = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

/* Availability for today + the next 3 days. Each slot:
   { value, label, ok, sub } — ok=false means it can't be booked. */
function useTourDays() {
  const [now] = useState(adelaideNow);
  const days = useMemo(() => [0, 1, 2, 3].map((n) => addDays(now.date, n)), [now.date]);
  const [avail, setAvail] = useState({}); // iso -> availability response | "error"

  useEffect(() => {
    let alive = true;
    days.forEach((d) => {
      fetch(`${API}/api/tours/availability/${d.iso}`)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error("availability"))))
        .then((data) => alive && setAvail((a) => ({ ...a, [d.iso]: data })))
        .catch(() => alive && setAvail((a) => ({ ...a, [d.iso]: "error" })));
    });
    return () => {
      alive = false;
    };
  }, [days]);

  const slotsFor = (iso, isToday) => {
    const data = avail[iso];
    return TOUR_TIMES.map((t) => {
      if (isToday && toMinutes(t.value) <= now.minutes) return { ...t, ok: false, sub: "DEPARTED" };
      if (!data) return { ...t, ok: false, loading: true, sub: "…" };
      // Couldn't check: let them choose; the booking page checks again
      if (data === "error") return { ...t, ok: true, sub: "CHECK TIMES" };
      if (data.blocked) return { ...t, ok: false, sub: "CLOSED" };
      if (!(data.available_times || []).includes(t.value)) return { ...t, ok: false, sub: "NO TOUR" };
      const left = data.time_slots?.[t.value]?.total_remaining;
      if (left != null && left <= 0) return { ...t, ok: false, sub: "SOLD OUT", left: 0 };
      return { ...t, ok: true, left, sub: left != null ? `${left} SPOT${left === 1 ? "" : "S"} LEFT` : "" };
    });
  };

  return { now, days, avail, slotsFor };
}

// ---- page --------------------------------------------------------------------

export default function RHomePage({ lead = "tours", showReviews = true }) {
  const hours = useOpeningHours();
  const pricing_ = useTourPricing();
  const { products } = useStoreProducts();
  const tour = useTourDays();
  const schemaData = { ...baseSchema, ...openingHoursSchema(hours) };
  const shopFirst = lead === "shop";

  const fromPrice = formatPrice(Math.min(pricing_.adult, pricing_.child));
  const openLine = (hours.summary && hours.summary[0]) || "Open daily 8:30am – 5:30pm";

  // "Next tour today · 2pm" from today's live schedule
  const nextTour = useMemo(() => {
    const todaySlots = tour.slotsFor(tour.days[0].iso, true);
    if (todaySlots.some((s) => s.loading)) return null;
    const today = todaySlots.find((s) => s.ok);
    if (today) return `today · ${today.label}`;
    const tomorrow = tour.slotsFor(tour.days[1].iso, false).find((s) => s.ok && !s.loading);
    return `tomorrow · ${tomorrow ? tomorrow.label : "10am"}`;
    // eslint-disable-next-line
  }, [tour.avail]);

  return (
    <MarketingShell pageClass="pg-home">
      <SEOHead
        title="Underground Opal Mine Tours & Authentic Australian Opals"
        description="Underground opal mine tours, a FREE museum and genuine Australian opals in Coober Pedy. Family-owned since the 1920s. Book your underground tour today!"
        keywords="opal mine tours, Coober Pedy tours, underground tours, Australian opals, opal shopping, mine museum, South Australia tourism, authentic opals, opal jewelry, underground experience"
        canonical="/"
        ogImage="/og-umoona.jpg"
        schemaData={schemaData}
      />

      <div className={"hm" + (shopFirst ? " hm--shop-first" : "")}>
        {/* HERO · two doors */}
        <section className="hm-hero">
          <div className="hm-doors">
            <a href="#tours" className="hm-door hm-door--tours">
              <img src="/design/assets/hero-cave.jpg" alt="Hand-dug tunnels of the Umoona mine" className="hm-door__bg" />
              <div className="hm-door__scrim" />
              <div className="hm-door__body">
                <div className="hm-live">
                  <span className="hm-live__dot" />
                  {nextTour ? `Next tour ${nextTour}` : "Daily tours · 10am, 2pm & 4pm"}
                </div>
                <h1 className="hm-h1">
                  GO
                  <br />
                  UNDER
                  <br />
                  GROUND
                </h1>
                <p className="hm-door__text hm-tall">
                  Walk hand-dug opal tunnels with a local guide. Daily tours at 10am, 2pm &amp; 4pm — 22°C below,
                  all year.
                </p>
                <div className="hm-door__cta">
                  <span className="hm-btn hm-btn--accent">
                    Book a Tour <ArrowRight size={17} />
                  </span>
                  <span className="hm-from">FROM {fromPrice}</span>
                </div>
              </div>
            </a>

            <Link to="/online-store" className="hm-door hm-door--shop">
              <div className="hm-door__photo">
                <img src="/design/assets/photo-opal.jpg" alt="Coober Pedy opal pendant" />
              </div>
              <div className="hm-door__panel">
                <div className="hm-eyebrow hm-eyebrow--rust">Online Opal Store · ships Australia-wide</div>
                <h2 className="hm-h2door">
                  TAKE HOME
                  <br />
                  OPAL
                </h2>
                <p className="hm-door__text hm-door__text--dark hm-tall">
                  Genuine Coober Pedy opal with a certificate of authenticity.
                </p>
                <div className="hm-door__cta">
                  <span className="hm-btn hm-btn--ink">
                    Shop Opal <ArrowRight size={17} />
                  </span>
                  {products.length > 0 && <span className="hm-count">{products.length} PIECES ONLINE</span>}
                </div>
              </div>
            </Link>
          </div>

          <div className="hm-strip">
            <span>Free museum entry</span>
            <span className="hm-strip__sep">·</span>
            <span className="hm-strip__teal">{openLine}</span>
            <span className="hm-strip__sep">·</span>
            <span className="hm-strip__gold">22°C underground, all year</span>
          </div>
        </section>

        <ToursSection tour={tour} pricing={pricing_} />
        <ShopSection products={products} />

        {/* ALSO AT UMOONA */}
        <section className="hm-also">
          <div className="hm-wrap">
            <div className="hm-eyebrow hm-eyebrow--dark hm-also__eyebrow">Also at Umoona</div>
            <div className="hm-also__grid">
              {ALSO.map(({ to, Icon, k, t, mod }) => (
                <Link key={to} to={to} className={`hm-alsocard hm-alsocard--${mod}`}>
                  <span className="hm-alsocard__icon">
                    <Icon size={22} />
                  </span>
                  <span className="hm-alsocard__text">
                    <span className="hm-alsocard__kick">{k}</span>
                    <span className="hm-alsocard__title">{t}</span>
                  </span>
                  <ArrowRight size={18} />
                </Link>
              ))}
            </div>
            {showReviews && (
              <div className="hm-also__grid hm-reviews">
                {REVIEWS.map((r) => (
                  <figure key={r.name} className="hm-review">
                    <div className="hm-review__stars">★★★★★</div>
                    <blockquote>“{r.q}”</blockquote>
                    <figcaption>{r.name} · Google</figcaption>
                  </figure>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </MarketingShell>
  );
}

// ---- Underground Tours -------------------------------------------------------

function ToursSection({ tour, pricing }) {
  const navigate = useNavigate();
  const [dayIdx, setDayIdx] = useState(0);
  const [time, setTime] = useState(null);
  const [adults, setAdults] = useState(2);
  const [kids, setKids] = useState(0);

  const day = tour.days[dayIdx];
  const slots = tour.slotsFor(day.iso, dayIdx === 0);
  const loaded = slots.every((s) => !s.loading);

  // Pick the first bookable time once the day's availability is in
  useEffect(() => {
    if (!loaded) return;
    if (!slots.some((s) => s.value === time && s.ok)) {
      const first = slots.find((s) => s.ok);
      setTime(first ? first.value : null);
    }
    // eslint-disable-next-line
  }, [loaded, dayIdx, tour.avail]);

  // Today sold out / departed → start on the next day with a tour
  useEffect(() => {
    if (dayIdx !== 0) return;
    const todayDone = tour.slotsFor(tour.days[0].iso, true).every((s) => !s.ok && !s.loading);
    if (!todayDone) return;
    const next = tour.days.findIndex((d, i) => i > 0 && tour.slotsFor(d.iso, false).some((s) => s.ok));
    if (next > 0) setDayIdx(next);
    // eslint-disable-next-line
  }, [tour.avail]);

  const slot = slots.find((s) => s.value === time);
  const cap = Math.min(MAX_GUESTS, slot && slot.left != null ? slot.left : MAX_GUESTS);
  const people = adults + kids;
  const total = adults * pricing.adult + kids * pricing.child;

  const book = () => {
    const q = new URLSearchParams({ date: day.iso, adults: String(adults), children: String(kids) });
    if (time) q.set("time", time);
    navigate(`/book-tour?${q}`);
  };

  return (
    <section id="tours" className="hm-tours">
      <div className="hm-wrap hm-tours__grid">
        <div className="hm-tours__main">
          <div>
            <div className="hm-eyebrow hm-eyebrow--dark">Underground Tours · daily</div>
            <h2 className="hm-h2 hm-h2--light">A century of opal mining, right underground</h2>
            <p className="hm-lede hm-lede--light">
              Follow original hand-dug drives, see opal still in the wall, and step inside a real underground home — led
              by guides who grew up in Coober Pedy.
            </p>
          </div>
          <div className="hm-includes">
            {INCLUDES.map(({ Icon, t, s }) => (
              <div key={t} className="hm-include">
                <Icon size={20} className="hm-include__icon" />
                <div>
                  <div className="hm-include__t">{t}</div>
                  <div className="hm-include__s">{s}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="hm-tours__photo">
            <img src="/design/assets/photo-minetour.jpg" alt="Guide leading a mine tour" loading="lazy" decoding="async" />
          </div>
        </div>

        <div className="hm-bookcard">
          <div className="hm-bookcard__head">
            <span className="hm-bookcard__title">Book your tour</span>
            <span className="hm-bookcard__dur">~1 HOUR</span>
          </div>

          <div className="hm-bookcard__lbl">Date</div>
          <div className="hm-days">
            {tour.days.map((d, i) => (
              <button
                key={d.iso}
                type="button"
                className={"hm-day" + (i === dayIdx ? " is-on" : "")}
                onClick={() => setDayIdx(i)}
                aria-pressed={i === dayIdx}
              >
                <span className="hm-day__dow">{i === 0 ? "Today" : d.dow}</span>
                <span className="hm-day__num">{d.num}</span>
              </button>
            ))}
          </div>

          <div className="hm-bookcard__lbl">Departure</div>
          <div className="hm-times">
            {slots.map((s) => (
              <button
                key={s.value}
                type="button"
                className={"hm-time" + (s.value === time ? " is-on" : "")}
                disabled={!s.ok}
                onClick={() => setTime(s.value)}
                aria-pressed={s.value === time}
              >
                <span className="hm-time__t">{s.label}</span>
                <span className="hm-time__sub">{s.sub}</span>
              </button>
            ))}
          </div>

          <div className="hm-tix">
            <TicketRow label="Adults" price={formatPrice(pricing.adult, { cents: true })} n={adults} min={1}
              onDec={() => setAdults((n) => Math.max(1, n - 1))} onInc={() => people < cap && setAdults((n) => n + 1)} canInc={people < cap} />
            <TicketRow label="Children (5–15)" price={formatPrice(pricing.child, { cents: true })} n={kids} min={0}
              onDec={() => setKids((n) => Math.max(0, n - 1))} onInc={() => people < cap && setKids((n) => n + 1)} canInc={people < cap} />
            <div className="hm-tix__row">
              <div className="hm-tix__info">
                <div className="hm-tix__label">Under 5</div>
                <div className="hm-tix__price">Free · no ticket needed</div>
              </div>
            </div>
          </div>

          <button type="button" className="hm-bookbtn" onClick={book} disabled={!time}>
            <span>
              {time
                ? `Book ${people} ${people === 1 ? "person" : "people"} · ${slot ? slot.label : ""}`
                : loaded
                ? "No tours left this day"
                : "Checking times…"}
            </span>
            <span>{formatPrice(total, { cents: true })}</span>
          </button>
          <div className="hm-bookcard__foot">
            <span>Free museum entry included</span>
            <Link to="/group-tours">School &amp; group tours →</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function TicketRow({ label, price, n, min, onDec, onInc, canInc }) {
  return (
    <div className="hm-tix__row">
      <div className="hm-tix__info">
        <div className="hm-tix__label">{label}</div>
        <div className="hm-tix__price">{price}</div>
      </div>
      <div className="hm-stepper">
        <button type="button" aria-label={`Fewer ${label.toLowerCase()}`} onClick={onDec} disabled={n <= min}>
          <Minus size={14} />
        </button>
        <span>{n}</span>
        <button type="button" aria-label={`More ${label.toLowerCase()}`} onClick={onInc} disabled={!canInc}>
          <Plus size={14} />
        </button>
      </div>
    </div>
  );
}

// ---- Online Opal Shop --------------------------------------------------------

function tagFor(product) {
  if (product.featured) return "FEATURED";
  const { price } = pricing(product);
  if (price > 0 && price < 100) return "UNDER $100";
  return null;
}

function ShopSection({ products }) {
  // Featured pieces first, then the latest in-stock opal jewellery with a photo
  const picks = useMemo(() => {
    const usable = products.filter((p) => !isOutOfStock(p) && !isSouvenir(p) && mainImage(p));
    const featured = usable.filter((p) => p.featured);
    const rest = sortProducts(usable.filter((p) => !p.featured), "latest");
    return [...featured, ...rest].slice(0, 6);
  }, [products]);
  const counts = useMemo(() => BROWSE.map((b) => products.filter(b.match).length), [products]);

  return (
    <section id="shop" className="hm-shop">
      <div className="hm-wrap">
        <div className="hm-shop__head">
          <div>
            <div className="hm-eyebrow hm-eyebrow--rust">Online Opal Store · straight from the miners</div>
            <h2 className="hm-h2">Genuine opal, wherever you are</h2>
            <p className="hm-lede">
              The same stones as our Coober Pedy showroom — every piece with a certificate of authenticity, dispatched
              Australia-wide.
            </p>
          </div>
          <Link to="/online-store" className="hm-btn hm-btn--ink hm-shop__all">
            <ShoppingBag size={17} />
            {products.length ? `Shop all ${products.length} pieces` : "Shop all pieces"}
          </Link>
        </div>

        <div className="hm-grid">
          <Link to={`${SHOP}?opal=Black`} className="hm-feature">
            <img src="/design/assets/prod-11.jpg" alt="Black opal ring" />
            <div className="hm-feature__scrim" />
            <div className="hm-feature__body">
              <div className="hm-feature__kick">THE RAREST OPAL</div>
              <div className="hm-feature__title">Black opal collection</div>
              <span className="hm-feature__link">
                Shop black opal <ArrowRight size={15} />
              </span>
            </div>
          </Link>
          {picks.length === 0
            ? [0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="hm-pcard hm-pcard--skel" aria-hidden="true" />)
            : picks.map((p, i) => {
                const tag = tagFor(p);
                return (
                  <Link key={p._id || p.id} to={productPath(p)} className={"hm-pcard" + (i >= 4 ? " hm-pcard--extra" : "")}>
                    <div className="hm-pcard__media">
                      <img src={mainImage(p)} alt={p.name} loading="lazy" decoding="async" />
                      {tag && <span className="hm-pcard__tag">{tag}</span>}
                    </div>
                    <div className="hm-pcard__body">
                      <span className="hm-pcard__kick">{kicker(p)}</span>
                      <span className="hm-pcard__name">{p.name}</span>
                      <span className="hm-pcard__price">
                        <PriceLabel product={p} />
                      </span>
                    </div>
                  </Link>
                );
              })}
        </div>

        <div className="hm-browse">
          <span className="hm-browse__lbl">Browse</span>
          {BROWSE.map((b, i) => (
            <Link key={b.label} to={b.href} className="hm-chip">
              {b.label}
              {counts[i] > 0 && <span>{counts[i]}</span>}
            </Link>
          ))}
        </div>

        <div className="hm-trust">
          {TRUST.map(({ Icon, t, s }) => (
            <div key={t} className="hm-trust__item">
              <Icon size={22} className="hm-trust__icon" />
              <div>
                <div className="hm-trust__t">{t}</div>
                <div className="hm-trust__s">{s}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
